import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.ts";
import { Act, Empty } from "../ui.tsx";

interface Item { id: string; listingId: string; kind: string; startDate: string; endDate: string; note: string | null; held: boolean; booking: { id: string; code: string; status: string; guest: string | null } | null }
interface Cal { listings: { id: string; title: { fr: string }; cityKey: string; ownerType: string }[]; items: Item[] }

const DAYS = 28;
const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (s: string, n: number) => iso(new Date(Date.parse(s + "T12:00:00Z") + n * 864e5));
const monday = (s: string) => { const d = new Date(s + "T12:00:00Z"); return addDays(s, -((d.getUTCDay() + 6) % 7)); };
const KIND: Record<string, string> = { booking: "Séjour", host_block: "Blocage", maintenance: "Travaux", ical: "Calendrier externe" };

export function CalendarPage() {
  const [start, setStart] = useState(() => monday(iso(new Date())));
  const [cal, setCal] = useState<Cal | null>(null);
  const [pick, setPick] = useState<Item | null>(null);
  const [blk, setBlk] = useState({ listingId: "", startDate: "", endDate: "", note: "" });
  const [err, setErr] = useState<string | null>(null);
  const end = addDays(start, DAYS);
  const load = useCallback(() => { api<Cal>(`/v1/staff/calendar?from=${start}&to=${end}`).then(setCal).catch(() => setCal({ listings: [], items: [] })); }, [start, end]);
  useEffect(load, [load]);
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(start, i)), [start]);
  const today = iso(new Date());

  const cell = (listingId: string, d: string) => cal?.items.find((i) => i.listingId === listingId && i.startDate <= d && d < i.endDate);

  async function block() {
    setErr(null);
    try {
      await api(`/v1/staff/listings/${blk.listingId}/blocks`, { body: { startDate: blk.startDate, endDate: blk.endDate, ...(blk.note.trim() ? { note: blk.note.trim() } : {}) } });
      setBlk({ ...blk, startDate: "", endDate: "", note: "" }); load();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <section>
      <header className="pagehead">
        <h1>Calendrier</h1>
        <p className="muted small">Séjours et blocages de tous les biens. Un blocage ferme les nuits aux clients (séjour du propriétaire, travaux, réservation prise ailleurs).</p>
      </header>
      <div className="quick" style={{ marginBottom: 16 }}>
        <button className="chip" onClick={() => setStart(addDays(start, -7 * 2))}>← 2 semaines</button>
        <button className="chip" onClick={() => setStart(monday(today))}>Aujourd'hui</button>
        <button className="chip" onClick={() => setStart(addDays(start, 7 * 2))}>2 semaines →</button>
      </div>

      {!cal ? <div className="skel" aria-hidden="true" /> : cal.listings.length === 0 ? <Empty>Aucun bien publié.</Empty> : (
        <div className="cal-wrap">
          <table className="cal">
            <thead>
              <tr><th className="sticky">Bien</th>{days.map((d) => <th key={d} className={d === today ? "today" : new Date(d + "T12:00:00Z").getUTCDay() % 6 === 0 ? "wknd" : ""}><span className="num">{d.slice(8)}</span><small>{d.slice(5, 7)}</small></th>)}</tr>
            </thead>
            <tbody>
              {cal.listings.map((l) => (
                <tr key={l.id}>
                  <th className="sticky"><span>{l.title.fr}</span><small className="muted">{l.cityKey}{l.ownerType === "platform" ? " · Sekani" : ""}</small></th>
                  {days.map((d) => {
                    const it = cell(l.id, d);
                    if (!it) return <td key={d} className={d === today ? "today" : ""} />;
                    const cls = it.kind === "booking" ? (it.held ? "held" : "stay") : "block";
                    return <td key={d} className={`${cls} ${d === it.startDate ? "first" : ""}`} onClick={() => setPick(it)} title={it.booking ? `${it.booking.code} · ${it.booking.guest ?? ""}` : KIND[it.kind]}>{d === it.startDate || d === start ? <span>{it.booking?.guest ?? KIND[it.kind]}</span> : null}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small" style={{ marginTop: 8 }}><span className="key stay" /> séjour <span className="key held" /> en attente <span className="key block" /> blocage</p>

      {pick && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>{KIND[pick.kind] ?? pick.kind} · {pick.startDate} → {pick.endDate}</b>
          {pick.booking ? <p className="small"><Link className="link" to={`/b/${pick.booking.id}`}>{pick.booking.code}</Link> · {pick.booking.guest}</p> : <p className="small muted">{pick.note ?? "Sans note"}</p>}
          <div className="quick">
            {(pick.kind === "host_block" || pick.kind === "maintenance") && <Act tone="danger" run={async () => { await api(`/v1/staff/listings/${pick.listingId}/blocks/${pick.id}`, { method: "DELETE" }); setPick(null); load(); }}>Lever le blocage</Act>}
            <button className="chip" onClick={() => setPick(null)}>Fermer</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <b>Bloquer des nuits</b>
        <div className="formrow">
          <label className="field"><span>Bien</span><select value={blk.listingId} onChange={(e) => setBlk({ ...blk, listingId: e.target.value })}><option value="">—</option>{cal?.listings.map((l) => <option key={l.id} value={l.id}>{l.title.fr}</option>)}</select></label>
          <label className="field"><span>Du (arrivée)</span><input type="date" value={blk.startDate} onChange={(e) => setBlk({ ...blk, startDate: e.target.value })} /></label>
          <label className="field"><span>Au (départ)</span><input type="date" value={blk.endDate} min={blk.startDate} onChange={(e) => setBlk({ ...blk, endDate: e.target.value })} /></label>
          <label className="field"><span>Note</span><input value={blk.note} maxLength={200} onChange={(e) => setBlk({ ...blk, note: e.target.value })} placeholder="Séjour du propriétaire" /></label>
        </div>
        <div className="quick"><Act disabled={!blk.listingId || !blk.startDate || !blk.endDate || blk.endDate <= blk.startDate} run={block}>Bloquer</Act>{err && <span className="err small" role="alert">{err}</span>}</div>
      </div>
    </section>
  );
}
